'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useLimitPaywallStore } from '@/shared/stores/limit-paywall-store';
import { sectionTarget, elementTarget } from '@/shared/assistant';
import { createLogger } from '@/shared/lib/logger';
import type {
  AssistantCommandBlock,
  AssistantHighlightElement,
  AssistantPaywallParams,
  AssistantSettingsSection,
} from '@/types/assistant';
import { useAssistantStore } from '../model/assistant.store';
import { applyPatchToForm } from '../lib/settings-patch';
import { highlightElement, waitForTarget } from '../lib/targets';
import { designPath } from '../lib/routes';

const logger = createLogger('Assistant');

/**
 * The design a reply's settings patch lands on. One object per streamed reply:
 * it starts as the design on screen and an `open_design` moves it.
 */
export interface AssistantPatchTarget {
  designId: string | null;
}

const str = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

/**
 * Executes the API's interface commands (ADR-001 §4) on this client's own
 * routes and components. Free commands run at once; an unknown command is a
 * no-op with a log line, so a command added on the server never breaks a
 * shipped build. Commands run only for a reply streaming now, never when an
 * old thread is replayed.
 */
export function useAssistantCommands() {
  const router = useRouter();
  const showPaywall = useLimitPaywallStore((s) => s.showPaywall);

  return useCallback(
    (
      block: AssistantCommandBlock,
      turn: { target: AssistantPatchTarget; undoKey: string }
    ) => {
      const params = block.params ?? {};
      const building = str(params.building_id);
      const design = str(params.design_id);

      switch (block.command) {
        case 'apply_render_settings': {
          // `params` is the patch itself. It is for the design the turn was
          // sent from, or the one an `open_design` earlier in this reply
          // opened (the form was not on screen, so the server picked the
          // design); the patch then waits for that form to load.
          const designId = turn.target.designId;
          if (!designId) {
            logger.warn('Settings patch without a design on screen');
            return;
          }
          const pending = { designId, patch: params, undoKey: turn.undoKey };
          if (!applyPatchToForm(pending)) {
            useAssistantStore.getState().setPendingPatch(pending);
          }
          return;
        }
        case 'open_building':
          if (building) router.push(`/project/${building}`);
          return;
        case 'open_design':
          if (building && design) {
            turn.target.designId = design;
            router.push(designPath(building, design, 'design'));
          }
          return;
        case 'open_render': {
          const render = str(params.render_id);
          if (building && design && render) {
            router.push(
              designPath(building, design, 'design', {
                angleId: str(params.angle_id),
                arrival: render,
              })
            );
          }
          return;
        }
        case 'open_estimate': {
          const estimate = str(params.estimate_id);
          if (building && design && estimate) {
            router.push(
              designPath(building, design, 'estimate', { arrival: estimate })
            );
          }
          return;
        }
        case 'open_album':
          if (building && design) {
            router.push(
              designPath(building, design, 'album', {
                arrival: str(params.album_id),
              })
            );
          }
          return;
        case 'open_settings_panel': {
          const section = str(params.section) as AssistantSettingsSection | null;
          const target = section ? sectionTarget(section) : elementTarget('settings_panel');
          void waitForTarget(target).then((element) => {
            if (!element) {
              logger.info('Settings target not on screen', { target });
              return;
            }
            highlightElement(element);
            // A section row opens its sub-panel through its own handler.
            if (section) element.click();
          });
          return;
        }
        case 'highlight': {
          const element = str(params.element) as AssistantHighlightElement | null;
          if (!element) return;
          void waitForTarget(elementTarget(element)).then((found) => {
            if (found) highlightElement(found);
            else logger.info('Highlight target not on screen', { element });
          });
          return;
        }
        case 'open_paywall': {
          const { paywall, building_id } = params as unknown as AssistantPaywallParams;
          if (!paywall) return;
          showPaywall(
            paywall,
            'ASSISTANT_LIMIT',
            building_id ? { project_id: building_id } : undefined
          );
          return;
        }
        default:
          logger.info('Ignoring unknown assistant command', {
            command: block.command,
          });
      }
    },
    [router, showPaywall]
  );
}
